import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { TipoProyectoViewModel } from "src/app/_core/models/centros/tipo-proyecto.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-tipo-proyecto-autocomplete",
    templateUrl: "./tipo-proyecto-autocomplete.component.html",
    styleUrls: ["./tipo-proyecto-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TipoProyectoAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TipoProyectoAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Tipo de Proyecto";
	@Input() placeholder = "Buscar tipo de proyecto por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción del tipo de proyecto";

	// Input para dependencia de actividad
	@Input() set actividadID(value: number | null) {
		this._actividadID = value;
		this.filterByActividad();
	}
	get actividadID(): number | null {
		return this._actividadID;
	}

	// Outputs
	@Output() tipoProyectoSelected = new EventEmitter<TipoProyectoViewModel>();

	// State
	private _actividadID: number | null = null;
	private allTiposProyecto: TipoProyectoViewModel[] = []; // Lista completa sin filtrar
	tiposProyecto: TipoProyectoViewModel[] = [];
	tipoProyectoId: number | null = null;
	selectedTipoProyecto: TipoProyectoViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadTiposProyecto();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.tipoProyectoId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Indica si el control está deshabilitado (por disabled o por falta de actividadID)
	 */
	get isDisabled(): boolean {
		return this.disabled || !this._actividadID;
	}

	/** Hint contextual: depende de actividad y de si hay tipos disponibles. */
	get currentHint(): string {
		if (!this._actividadID) {
			return "Primero seleccione una actividad";
		}
		if (this.allTiposProyecto.length > 0 && this.tiposProyecto.length === 0) {
			return "No hay tipos de proyecto para esta actividad";
		}
		return this.hint;
	}

	/**
	 * Carga la lista de tipos de proyecto desde la API
	 */
	private loadTiposProyecto(): void {
		this.masterService
			.getTiposProyecto()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (tiposProyecto) => {
					this.allTiposProyecto = tiposProyecto; // Guardar lista completa
					this.filterByActividad(); // Aplicar filtro por actividad
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los tipos de proyecto",
					);
				},
			});
	}

	/**
	 * Filtra los tipos de proyecto según la actividad seleccionada
	 */
	private filterByActividad(): void {
		if (!this._actividadID) {
			this.tiposProyecto = [];
			this.tipoProyectoId = null;
			this.onChange(null);
		} else {
			// Filtrar tipos de proyecto por actividadID
			this.tiposProyecto = this.allTiposProyecto.filter(
				(tp) => tp.actividadID === this._actividadID,
			);

			// Si el tipo de proyecto actual no pertenece a la nueva actividad, limpiar.
			// Solo limpiar si la lista maestra YA ha cargado; si está vacía es porque
			// el async no ha terminado y no queremos borrar un valor heredado (ej. desde oportunidad).
			if (this.tipoProyectoId && this.allTiposProyecto.length > 0) {
				const tipoActual = this.tiposProyecto.find(
					(tp) => tp.id === this.tipoProyectoId,
				);
				if (!tipoActual) {
					this.tipoProyectoId = null;
					this.onChange(null);
				}
			}
		}

		this.syncSelected();
		this.cdr.markForCheck();
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedTipoProyecto =
			this.tipoProyectoId != null
				? (this.tiposProyecto.find(
						(tp) => tp.id === this.tipoProyectoId,
					) ?? null)
				: null;
	}

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(tipoProyecto: TipoProyectoViewModel | null): void {
		this.tipoProyectoId = tipoProyecto ? tipoProyecto.id : null;
		this.selectedTipoProyecto = tipoProyecto;
		this.onChange(this.tipoProyectoId);
		this.onTouched();
		if (tipoProyecto) {
			this.tipoProyectoSelected.emit(tipoProyecto);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}

	/**
	 * Función de display para el autocomplete
	 */
	displayTipoProyecto = (
		tipoProyecto: TipoProyectoViewModel | string,
	): string => {
		if (typeof tipoProyecto === "string") {
			return tipoProyecto;
		}
		return tipoProyecto && tipoProyecto.codigo && tipoProyecto.descripcion
			? `${tipoProyecto.codigo} - ${tipoProyecto.descripcion}`
			: "";
	};
}
